import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getApiClientConfig, type SuccessResponse } from '@access-cms/api-client';
import { ApiPrefix, HttpMethod } from '@access-cms/shared-constants';

import { queryClient } from '../../main';
import logger from '../../utils/logger';

/**
 * Single role assignment scoped to one contract. Source of truth:
 * docs/api/auth-api-spec.yaml (UserContractRole).
 */
export type ContractRole = {
  contract_id: string;
  contract_name?: string;
  contract_number?: string;
  role_type: 'COR' | 'FEDERAL_APPROVER' | 'FOREIGN_NATIONAL_APPROVER' | 'ROSTER_OWNER';
  role_variant?: 'PRIMARY' | 'ALTERNATE' | 'DELEGATE' | null;
  assigned_at?: string;
  /** True when this assignment currently grants contract access. */
  is_effective?: boolean;
};

/**
 * Current authenticated user returned by GET /api/v1/auth/me. Mirrors the
 * CurrentUser schema in docs/api/auth-api-spec.yaml.
 *
 * TODO(api-types): replace with components['schemas']['CurrentUser'] from
 * @access-cms/api-types once the auth service openapi.json is part of the
 * aggregated types on main. As of 2026-05-03 the aggregated types do not
 * include auth schemas, and INCLUDE_PLANNED_OPENAPI=1 pulls in 9 unrelated
 * specs (+22k lines), so we mirror the planned shape locally.
 */
export type CurrentUser = {
  id: string;
  email: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';
  user_type: 'EMPLOYEE' | 'CONTRACTOR';
  profile?: {
    cms_id?: string;
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    phone_number?: string;
    is_us_citizen?: boolean;
    /**
     * Legacy camelCase fields. The auth service `/me` handler currently returns
     * profile.firstName / profile.lastName (camelCase) instead of the snake_case
     * fields defined in docs/api/auth-api-spec.yaml.
     *
     * TODO(backend): once the /me handler is aligned with the spec, drop these
     * two legacy fields and switch consumers (AuthenticatedWelcomeRow) to use
     * first_name / last_name.
     */
    firstName?: string;
    lastName?: string;
  };
  ial_level: 'IAL1' | 'IAL2';
  aal_level: 'AAL1' | 'AAL2';
  okta_id?: string;
  last_login_at?: string;
  created_at: string;
  source_type?: 'EUA' | 'EUA-AD' | 'cmsidm';
  roles?: ContractRole[];
  system_roles?: string[];
};

interface AuthContextType {
  user: CurrentUser | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  handleLogin: (postAuthRedirectUri?: string) => void;
  handleLogout: () => Promise<void>;
  handleSessionExpired: () => Promise<void>;
  extendSession: () => Promise<boolean>;
  verifyAuth: () => Promise<void>;
}

const API_PREFIX = 'accesscms:';
const AUTH_SESSION_HINT_KEY = `${API_PREFIX}auth-hint`;

/** Resolved at call time so main.tsx configureApiClient() has already run. */
const getAuthApiBaseUrl = () => `${getApiClientConfig().baseUrl ?? ''}${ApiPrefix.AUTH}`;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const removeMatchingKeys = (storage: Storage, match: (k: string) => boolean) => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && match(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
};

const clearClientSession = () => {
  try {
    queryClient.clear();
    removeMatchingKeys(localStorage, (k) => k.startsWith(API_PREFIX));
    removeMatchingKeys(sessionStorage, (k) => k.startsWith(API_PREFIX));
  } catch (error) {
    console.error('Error clearing client-side data:', error);
  }
};

const readSessionHint = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem(AUTH_SESSION_HINT_KEY) !== null;
};

/**
 * Rewrite this history entry to `/` and opt the document out of the back/forward
 * cache so Back after logout is a fresh public landing load, not the frozen
 * mid-logout React tree (isLoading spinner + authenticated queries).
 */
const prepareHistoryForPostLogoutBack = () => {
  window.history.replaceState(window.history.state, '', `${window.location.origin}/`);
};

const extendSession = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${getAuthApiBaseUrl()}/me`, {
      credentials: 'include',
      method: HttpMethod.GET,
    });

    if (response.ok) {
      return true;
    }

    return false;
  } catch (error) {
    console.warn('Session extension failed:', error);

    return false;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasVerifiedAuthRef = useRef(false);
  const [hasSessionHint, setHasSessionHint] = useState(() => readSessionHint());

  // Background re-verifies (focus, bfcache restore) must not flip isLoading:
  // RequireCapability renders null while loading, which unmounts the page and
  // discards in-progress form state.
  const verifyAuth = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!background) {
      setIsLoading(true);
    }
    try {
      const response = await fetch(`${getAuthApiBaseUrl()}/me`, {
        credentials: 'include',
        method: HttpMethod.GET,
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
        setHasSessionHint(true);
        localStorage.setItem(AUTH_SESSION_HINT_KEY, 'true');
      } else {
        setUser(null);
        setHasSessionHint(false);
        localStorage.removeItem(AUTH_SESSION_HINT_KEY);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setHasSessionHint(false);
      localStorage.removeItem(AUTH_SESSION_HINT_KEY);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!hasSessionHint) {
      setIsInitialized(true);

      return;
    }

    if (!hasVerifiedAuthRef.current && !isInitialized && !isLoading) {
      hasVerifiedAuthRef.current = true;
      void verifyAuth();
    }
  }, [hasSessionHint, isInitialized, isLoading, verifyAuth]);

  // Safety net when a browser still restores the mid-logout page from bfcache.
  // Without a session, patching React state is not enough (isLoading stays true
  // and stale queries keep firing). Load the public landing as a new document.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }

      if (!readSessionHint()) {
        setIsLoading(false);
        setIsLoggingOut(false);
        setUser(null);
        setHasSessionHint(false);
        setIsInitialized(true);
        window.location.replace(window.location.origin);

        return;
      }

      setIsLoggingOut(false);
      void verifyAuth({ background: true });
    };

    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [verifyAuth]);

  // OOO-based permissions can change while this tab is in the background.
  // Refresh the role snapshot when the user returns so Alternate COR access
  // appears and disappears with the effective schedule without another login.
  useEffect(() => {
    const onFocus = () => {
      if (readSessionHint()) {
        void verifyAuth({ background: true });
      }
    };

    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [verifyAuth]);

  const handleLogin = (postAuthRedirectUri?: string) => {
    setIsLoading(true);
    setHasSessionHint(true);
    localStorage.setItem(AUTH_SESSION_HINT_KEY, 'pending');

    const loginUrl = new URL(`${getAuthApiBaseUrl()}/login`, window.location.origin);
    if (postAuthRedirectUri) {
      loginUrl.searchParams.set('redirect_uri', postAuthRedirectUri);
    }

    window.location.href = loginUrl.toString();
  };

  const clearLocalAuthState = () => {
    clearClientSession();
    setHasSessionHint(false);
    localStorage.removeItem(AUTH_SESSION_HINT_KEY);
    setUser(null);
  };

  /** Idle timeout: clear AccessCMS client state only (no CMS IDM signout). */
  const handleSessionExpired = async () => {
    setIsLoggingOut(true);
    clearLocalAuthState();
    prepareHistoryForPostLogoutBack();
    window.location.href = window.location.origin;
  };

  /** Explicit logout: clear local state, then navigate to CMS IDM signout URL from auth response. */
  const handleLogout = async () => {
    setIsLoading(true);
    setIsLoggingOut(true);

    let didStartBrowserNavigation = false;
    const postLogoutReturnOrigin = window.location.origin;

    try {
      const logoutUrl = new URL(`${getAuthApiBaseUrl()}/logout`, window.location.origin);
      logoutUrl.searchParams.set('redirect_uri', postLogoutReturnOrigin);

      const response = await fetch(logoutUrl.toString(), {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
        method: HttpMethod.POST,
      });

      clearLocalAuthState();
      prepareHistoryForPostLogoutBack();
      didStartBrowserNavigation = true;

      if (!response.ok) {
        logger.error({ status: response.status }, '[AUTH] Logout request failed');
        window.location.href = postLogoutReturnOrigin;

        return;
      }

      const body = (await response.json()) as SuccessResponse<'/api/v1/auth/logout', 'post'>;
      window.location.href = body.data.redirect_uri || postLogoutReturnOrigin;
    } catch (error) {
      logger.error({ error: error instanceof Error ? error.message : String(error) }, '[AUTH] Logout request error');
      clearLocalAuthState();
      prepareHistoryForPostLogoutBack();
      didStartBrowserNavigation = true;
      window.location.href = postLogoutReturnOrigin;
    } finally {
      if (!didStartBrowserNavigation) {
        setIsLoading(false);
        setIsLoggingOut(false);
      }
    }
  };

  const value: AuthContextType = {
    extendSession,
    handleLogin,
    handleLogout,
    handleSessionExpired,
    isAuthenticated: !!user,
    isInitialized,
    isLoading,
    isLoggingOut,
    user,
    verifyAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
